import { Toaster } from "@/components/ui/toaster"
import { ThemeProvider } from "@/lib/ThemeContext"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import Layout from './components/Layout';
import PageTransition from '@/components/PageTransition';

// Lazy-loaded routes — code-split to reduce initial bundle size
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Home = lazy(() => import('./pages/Home'));

// Lazy-loaded routes — code-split to reduce initial bundle size
const Admin = lazy(() => import('./pages/Admin'));
const Products = lazy(() => import('./pages/admin/Products'));
const Reservations = lazy(() => import('./pages/admin/Reservations'));
const Customers = lazy(() => import('./pages/admin/Customers'));
const AddOns = lazy(() => import('./pages/admin/AddOns'));
const Availability = lazy(() => import('./pages/admin/Availability'));
const WaterSlides = lazy(() => import('./pages/WaterSlides'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const BookingConfirmation = lazy(() => import('./pages/BookingConfirmation'));
const WaterSlidesPage = lazy(() => import('./pages/WaterSlidesPage'));
const PartyRentalsPage = lazy(() => import('./pages/PartyRentalsPage'));
const BrowseRentalsPage = lazy(() => import('./pages/BrowseRentalsPage'));
const ServiceArea = lazy(() => import('./pages/ServiceArea'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const AddOnsPage = lazy(() => import('./pages/AddOnsPage'));
const Profile = lazy(() => import('./pages/Profile'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const EmailPreview = lazy(() => import('./pages/EmailPreview'));
const Register = lazy(() => import('./pages/Register'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OAuthConsent = lazy(() => import('./pages/OAuthConsent'));
// Add page imports here

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  const location = useLocation();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-muted border-t-foreground rounded-full animate-spin" />
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <Suspense fallback={
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-muted border-t-foreground rounded-full animate-spin" />
      </div>
    }>
        <Routes location={location} key={location.key}>
          <Route element={<Layout />}>
            <Route path="/" element={<PageTransition><Home /></PageTransition>} />
            <Route path="/Login" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ForgotPassword" element={<ForgotPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/wsh" element={<Admin />} />
            <Route path="/admin/products" element={<Products />} />
            <Route path="/admin/reservations" element={<Reservations />} />
            <Route path="/admin/customers" element={<Customers />} />
            <Route path="/admin/addons" element={<AddOns />} />
            <Route path="/admin/availability" element={<Availability />} />
            <Route path="/WaterSlides" element={<PageTransition><WaterSlides /></PageTransition>} />
            <Route path="/water-slides-rentals" element={<PageTransition><WaterSlidesPage /></PageTransition>} />
            <Route path="/party-rentals" element={<PageTransition><PartyRentalsPage /></PageTransition>} />
            <Route path="/browse-rentals" element={<PageTransition><BrowseRentalsPage /></PageTransition>} />
            <Route path="/service-area" element={<PageTransition><ServiceArea /></PageTransition>} />
            <Route path="/about-us" element={<PageTransition><AboutUs /></PageTransition>} />
            <Route path="/add-ons" element={<PageTransition><AddOnsPage /></PageTransition>} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/product/:productId" element={<PageTransition><ProductDetail /></PageTransition>} />
            <Route path="/booking/confirmation" element={<PageTransition><BookingConfirmation /></PageTransition>} />
            <Route path="/payment-success" element={<PageTransition><PaymentSuccess /></PageTransition>} />
            <Route path="/email-preview" element={<EmailPreview />} />
            {/* Add your page Route elements here */}
          </Route>
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <AuthenticatedApp />
          </Router>
          <Toaster />
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App